import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Ref,
  type RefObject,
} from 'react';
import {
  ArrowUpToLine,
  CheckCircle2,
  ChevronDown,
  CircleStop,
  Images,
  Loader2,
  MessageCircleQuestion,
  XCircle,
} from 'lucide-react';
import {
  SectionNav,
  type SectionNavHandle,
  type SectionNavItem,
} from '@reflex/ui/components/layout/SectionNav';
import { ImageLightbox, type LightboxImage } from '@reflex/ui/components/content/ImagePreview';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { cn } from '@reflex/ui/lib/utils';
import { useIsScrollable } from './use-rail-room.js';
import type { ChatItem, TurnBlock } from './types.js';
import { TurnHoverPreview } from './turn-hover-preview.js';
import {
  buildTurnNav,
  CHAT_END_ANCHOR_ID,
  CHAT_START_ANCHOR_ID,
  MAX_RAIL_TURNS,
  type TurnEntry,
  type TurnNav,
  type TurnStatus,
} from './turn-nav.js';
import {
  buildUnifiedTurnRail,
  type SessionIndexTurn,
  type UnifiedTurnEntry,
} from './unified-turn-nav.js';

interface ChatTurnNavProps {
  /** The transcript rows currently mounted, in transcript order. */
  mountedMessages: ChatItem[];
  /** Blocks of the turn still streaming, which belong to the newest prompt. */
  liveBlocks?: readonly TurnBlock[];
  /** True while the agent is working, so the newest turn reads as in progress. */
  isGenerating?: boolean;
  /** The transcript's scroll container, which the nav scrolls and observes. */
  scrollRootRef: RefObject<HTMLElement | null>;
  /**
   * True when the transcript window is holding older rows out of the DOM. Those
   * turns cannot be listed — their anchors do not exist — but the rail still
   * owes the reader a way back to the "Show earlier messages" button.
   */
  hasEarlierMessages?: boolean;
  /**
   * The session index's prompt rows, whole session in session order. When
   * present the rail goes index-first: one continuous list of every turn,
   * merging these rows with the mounted turns (`buildUnifiedTurnRail`).
   * Selecting an unloaded row loads history toward it (`onSelectUnloaded`),
   * with a spinner on the row being loaded (`loadingSeq`). Absent for legacy
   * agents, whose rail lists only mounted turns exactly as before.
   */
  sessionIndexTurns?: {
    turns: SessionIndexTurn[];
    onSelectUnloaded: (seqNumber: number) => void;
    loadingSeq: number | null;
  };
  /**
   * Called before the nav scrolls to a turn. The transcript auto-scrolls itself
   * to the bottom, so it has to let go of that before a jump can land.
   */
  onSelect?: () => void;
  /** Show the alt+arrow hint. Set where those keys are actually bound. */
  showHotkeyHint?: boolean;
  className?: string;
  /**
   * Handle onto the rail's highlight, for the host that owns the alt+arrow
   * stepping: the keys move the transcript directly, so the rail has to be told
   * where they went rather than working it out from the scroll.
   */
  ref?: Ref<SectionNavHandle>;
}

/**
 * Most rail rows rendered at once in unified mode. The whole session stays in
 * the rail's data, but not in its DOM: a ten-thousand-turn session must not
 * mean ten thousand rows. The window is a plain slice centered on where the
 * reader is — the row being loaded toward, else the newest loaded turn — with
 * an edge row at either end carrying the hidden count and shifting the window
 * on click. The simplest bound that keeps long sessions cheap, with no
 * virtualization library.
 */
const RAIL_WINDOW_ROWS = 60;

/**
 * How each outcome reads at rail size. The question and error states are the
 * ones worth spotting from across the thread, so they get the only colours;
 * a finished turn is the norm and stays quiet.
 */
const STATUS_GLYPH: Record<
  TurnStatus,
  { icon: ComponentType<{ className?: string }>; className: string; label: string } | null
> = {
  question: {
    icon: MessageCircleQuestion,
    className: 'text-status-warning',
    label: 'waiting on an answer',
  },
  error: { icon: XCircle, className: 'text-status-error', label: 'ended with an error' },
  interrupted: { icon: CircleStop, className: 'text-muted-foreground/80', label: 'interrupted' },
  working: { icon: Loader2, className: 'animate-spin text-primary', label: 'in progress' },
  done: { icon: CheckCircle2, className: 'text-muted-foreground/50', label: 'done' },
  // A prompt sent while the agent was busy: nothing has happened yet, and a
  // glyph promising otherwise would be a lie.
  unanswered: null,
};

/**
 * Rebuild the rail only when the turns themselves change.
 *
 * Two layers, because the transcript re-renders on every streamed event and a
 * fully revealed thread can be thousands of rows: the `useMemo` keeps the scan
 * off renders whose inputs did not change, and the identity cache keeps
 * `SectionNav` from tearing down and re-attaching its IntersectionObserver
 * mid-stream when the scan produced the same turns as last time.
 */
function useStableTurnNav(
  mountedMessages: ChatItem[],
  liveBlocks: readonly TurnBlock[] | undefined,
  isGenerating: boolean | undefined,
  maxTurns: number,
): TurnNav {
  const nav = useMemo(
    () => buildTurnNav(mountedMessages, { blocks: liveBlocks, isGenerating }, maxTurns),
    [mountedMessages, liveBlocks, isGenerating, maxTurns],
  );
  const key = JSON.stringify([
    nav.totalTurns,
    nav.truncated,
    nav.turns.map((turn) => [turn.id, turn.label, turn.status, turn.images.length]),
  ]);
  const cache = useRef({ key, nav });
  if (cache.current.key !== key) cache.current = { key, nav };
  return cache.current.nav;
}

/** The status glyph, and — when the turn produced any — the button into its images. */
function TurnAccessories({
  turn,
  number,
  onOpenImages,
}: {
  turn: TurnEntry;
  /**
   * The number the rail shows for this turn — the unified session number when
   * the rail is index-backed. Null for a steer, which has no number of its
   * own: borrowing one (say, its mounted position) would announce another
   * turn's number, so its accessories name it as a steer instead.
   */
  number: number | null;
  onOpenImages: (turn: TurnEntry, number: number | null) => void;
}) {
  const glyph = STATUS_GLYPH[turn.status];
  const count = turn.images.length;
  const subject = number === null ? 'Steer' : `Turn ${number}`;
  const source = number === null ? 'the steer prompt' : `turn ${number}`;
  return (
    <>
      {glyph ? (
        <span
          role="img"
          aria-label={`${subject} ${glyph.label}`}
          title={glyph.label}
          className="flex shrink-0 items-center"
        >
          <glyph.icon className={cn('size-3', glyph.className)} aria-hidden />
        </span>
      ) : null}
      {count > 0 && (
        <button
          type="button"
          onClick={() => onOpenImages(turn, number)}
          // A count beside the glyph is what tells a reader there is a gallery
          // behind it rather than a single screenshot.
          className="inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-[10px] tabular-nums text-muted-foreground/70 transition-colors hover:bg-muted-foreground/15 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={
            count === 1 ? `View the image from ${source}` : `View ${count} images from ${source}`
          }
        >
          <Images className="size-3" aria-hidden />
          {count > 1 && count}
        </button>
      )}
    </>
  );
}

/**
 * Turn rail beside the transcript: the recent prompts, numbered, each carrying
 * how its turn went and a way into any images it produced, so the rail reads as
 * a report on the work and not only an index of prompts. Highlights the turn in
 * view, follows it as the reader scrolls, and jumps to the one clicked — the
 * same `SectionNav` the settings and admin pages use, pointed at the chat's own
 * scroll container instead of the page's.
 *
 * Jumps to the ends of the chat are earned, not fixtures: one appears only when
 * turns are hidden above, the other only when the thread actually scrolls.
 *
 * Deliberately short: a dozen rows that track where you are, not an index of
 * everything. Always rendered, down to a fresh thread with nothing in it, which
 * gets the header and "No turns yet": the rail is furniture, and furniture that
 * arrives on the first prompt shifts the gutter under the reader's pointer. The
 * host decides whether there is room for it at all — on a phone it is never
 * rendered.
 */
export const ChatTurnNav = memo(function ChatTurnNav({
  mountedMessages,
  liveBlocks,
  isGenerating,
  scrollRootRef,
  hasEarlierMessages,
  sessionIndexTurns,
  onSelect,
  showHotkeyHint,
  className,
  ref,
}: ChatTurnNavProps) {
  // Index-first when the session index knows any turns: the rail then lists
  // the whole session as one continuous list rather than a window onto the
  // mounted turns.
  const unified =
    sessionIndexTurns && sessionIndexTurns.turns.length > 0 ? sessionIndexTurns : null;
  // Unified mode drops `MAX_RAIL_TURNS` and scrolls instead: the whole point
  // is one list of every turn in the session, unloaded rows are plain text
  // with no IntersectionObserver cost (they have no anchors), and the loaded
  // turns are bounded by how many pages the reader has actually pulled in —
  // not by how long the session grew. Legacy agents keep the fixed cap: their
  // "Show earlier messages" mounts unbounded history in one step.
  const { turns, totalTurns } = useStableTurnNav(
    mountedMessages,
    liveBlocks,
    isGenerating,
    unified ? Number.POSITIVE_INFINITY : MAX_RAIL_TURNS,
  );
  const rail = useMemo(
    () => (unified ? buildUnifiedTurnRail(unified.turns, turns) : null),
    [unified, turns],
  );
  // Both end jumps are conditional: each earns its row only by reaching
  // somewhere the listed turns cannot. With every turn listed and the whole
  // thread on screen, the rail is just the turns.
  const canScroll = useIsScrollable(scrollRootRef);
  const [gallery, setGallery] = useState<{ images: LightboxImage[]; title: string } | null>(null);
  // Rows the render window is offset from its natural center, moved by the
  // hidden-count edge rows and cleared by a jump (which recenters on its
  // target). Clamped at use, so a stale value cannot push the window off the
  // list.
  const [railShift, setRailShift] = useState(0);

  // Legacy rail only. Turns above the rail's cap are counted exactly; turns
  // the transcript window dropped are not counted at all, since their rows are
  // gone. When both are in play the number would understate what is up there,
  // so the row goes wordless rather than wrong. The unified rail lists every
  // turn itself — hidden or unloaded ones as muted rows — so it never needs
  // the jump.
  const cappedTurns = totalTurns - turns.length;
  const startJumpLabel = rail
    ? null
    : hasEarlierMessages
      ? 'Earlier turns'
      : cappedTurns > 0
        ? `${cappedTurns} earlier ${cappedTurns === 1 ? 'turn' : 'turns'}`
        : null;

  const openImages = useCallback(
    (turn: TurnEntry, number: number | null) =>
      setGallery({
        images: turn.images,
        title: number === null ? `Steer: ${turn.label}` : `Turn ${number}: ${turn.label}`,
      }),
    [],
  );

  const sections = useMemo((): SectionNavItem[] => {
    const mapRailEntry = (entry: UnifiedTurnEntry): SectionNavItem => {
      if (entry.kind === 'unloaded') {
        return {
          id: `chat-turn-unloaded-${entry.seqNumber}`,
          label: entry.label,
          prefix: String(entry.number),
          dimmed: true,
          onActivate: () => {
            // Recenter the render window on the jump target, wherever the
            // edge rows had walked it.
            setRailShift(0);
            unified?.onSelectUnloaded(entry.seqNumber);
          },
          trailing:
            unified?.loadingSeq === entry.seqNumber ? (
              <Loader2 className="size-3 shrink-0 animate-spin text-primary" aria-hidden />
            ) : undefined,
        };
      }
      return {
        id: entry.turn.id,
        label: entry.turn.label,
        // A steer is a prompt but not a turn (the index is canonical), so
        // its row carries no number — in its accessories either.
        ...(entry.number !== null ? { prefix: String(entry.number) } : {}),
        ...(entry.turn.title ? { title: <TurnHoverPreview content={entry.turn.content} /> } : {}),
        trailing: (
          <TurnAccessories turn={entry.turn} number={entry.number} onOpenImages={openImages} />
        ),
      };
    };

    let windowedRail: SectionNavItem[] | null = null;
    if (rail) {
      // Bound the DOM to `RAIL_WINDOW_ROWS` rows (see its comment): slice
      // around the reader's focus — the row history is loading toward when a
      // jump is in flight, else the newest loaded turn — offset by however
      // far the edge rows have walked the window.
      const { entries } = rail;
      let start = 0;
      let end = entries.length;
      if (entries.length > RAIL_WINDOW_ROWS) {
        const loadingIndex =
          unified?.loadingSeq == null
            ? -1
            : entries.findIndex(
                (entry) => entry.kind === 'unloaded' && entry.seqNumber === unified.loadingSeq,
              );
        let lastLoaded = entries.length - 1;
        while (lastLoaded >= 0 && entries[lastLoaded].kind !== 'loaded') lastLoaded -= 1;
        const focus = loadingIndex !== -1 ? loadingIndex : lastLoaded !== -1 ? lastLoaded : 0;
        const clamp = (row: number) =>
          Math.max(0, Math.min(row, entries.length - RAIL_WINDOW_ROWS));
        // Clamp before applying the shift, so each edge-row click moves the
        // window a full stride even from a window pinned at either end.
        start = clamp(clamp(focus - Math.floor(RAIL_WINDOW_ROWS / 2)) + railShift);
        end = start + RAIL_WINDOW_ROWS;
      }
      const stride = Math.floor(RAIL_WINDOW_ROWS / 2);
      windowedRail = [
        ...(start > 0
          ? [
              {
                id: 'chat-turn-window-earlier',
                label: `${start} earlier ${start === 1 ? 'turn' : 'turns'}`,
                dimmed: true,
                onActivate: () => setRailShift((shift) => shift - stride),
              } satisfies SectionNavItem,
            ]
          : []),
        ...entries.slice(start, end).map(mapRailEntry),
        ...(end < entries.length
          ? [
              {
                id: 'chat-turn-window-later',
                label: `${entries.length - end} later ${entries.length - end === 1 ? 'turn' : 'turns'}`,
                dimmed: true,
                onActivate: () => setRailShift((shift) => shift + stride),
              } satisfies SectionNavItem,
            ]
          : []),
      ];
    }

    const turnItems: SectionNavItem[] = windowedRail
      ? windowedRail
      : turns.map(
          (turn): SectionNavItem => ({
            id: turn.id,
            label: turn.label,
            prefix: String(turn.number),
            ...(turn.title ? { title: <TurnHoverPreview content={turn.content} /> } : {}),
            trailing: (
              <TurnAccessories turn={turn} number={turn.number} onOpenImages={openImages} />
            ),
          }),
        );
    return [
      ...(startJumpLabel
        ? [
            {
              id: CHAT_START_ANCHOR_ID,
              label: startJumpLabel,
              icon: ArrowUpToLine,
              pinned: 'start',
            } satisfies SectionNavItem,
          ]
        : []),
      ...turnItems,
      ...(canScroll
        ? [
            {
              id: CHAT_END_ANCHOR_ID,
              label: 'Jump to bottom',
              icon: ChevronDown,
              pinned: 'end',
            } satisfies SectionNavItem,
          ]
        : []),
    ];
  }, [rail, unified, turns, railShift, startJumpLabel, canScroll, openImages]);

  return (
    <div className={cn('flex flex-col items-stretch gap-1', className)}>
      <SectionNav
        sections={sections}
        label="Turns"
        total={rail ? rail.totalTurns : totalTurns}
        // A thread that has not started yet still gets the rail: it is part of
        // the pane, not a reward for a busy thread. Appearing on the first
        // prompt would move the gutter under the reader's pointer and leave
        // them wondering where it had been.
        emptyState="No turns yet"
        appearance="list"
        // Turn anchors are per-message ids on an append-only transcript: they
        // would not survive a reload, and one history entry per click would bury
        // the agent page's back button.
        syncHash={false}
        scrollRootRef={scrollRootRef}
        onSelect={onSelect}
        scrollBehavior="auto"
        className="min-h-0"
        data-testid="chat-turn-nav"
        ref={ref}
      />
      {/* The hint promises the arrows go somewhere, so it waits until they do. */}
      {showHotkeyHint && sections.length > 0 && (
        <span className="flex items-center gap-1 pl-3 pt-1 text-3xs text-muted-foreground/70">
          <KbdCombo keys={['alt', 'up', 'down']} size="xs" />
          navigate
        </span>
      )}
      {gallery && (
        <ImageLightbox
          images={gallery.images}
          open
          onOpenChange={(open) => !open && setGallery(null)}
        />
      )}
    </div>
  );
});
